import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { cn } from '@rekrytering/ui/lib/utils'
import { useForm } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authClient } from '@/lib/auth-client'
import type { User } from '~/types'

type RemoveTarget = {
  readonly userId: string
  readonly email: string
  readonly displayName: string
}

const roleBadgeClass = (role: string): string => {
  if (role === 'owner')
    return 'bg-primary/10 text-primary border border-primary/20'
  if (role === 'admin')
    return 'bg-secondary text-secondary-foreground border border-border'
  return 'bg-transparent text-primary/60 border border-border'
}

const ROLE_LABEL_KEY = {
  owner: 'settings.team.roleOwner',
  admin: 'settings.team.roleAdmin',
  member: 'settings.team.roleMember',
} as const

const roleTranslationKey = (
  role: string,
): (typeof ROLE_LABEL_KEY)[keyof typeof ROLE_LABEL_KEY] =>
  ROLE_LABEL_KEY[role as keyof typeof ROLE_LABEL_KEY] ?? ROLE_LABEL_KEY.member

export function TeamSettingsPage({ user }: { user: User }) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | undefined>(
    undefined,
  )
  const [isRemoving, setIsRemoving] = useState(false)

  const isOwner = user.role === 'owner'

  const { data: members, refetch: refetchMembers } = useQuery(
    convexQuery(api.features.team.team.listMyTeamMembers, {}),
  )

  const form = useForm({
    defaultValues: {
      email: '',
      role: 'member' as 'member' | 'admin',
    },
    onSubmit: async ({ value }) => {
      if (!user.organizationId) return
      setIsSubmitting(true)
      setInviteSent(false)
      await authClient.organization.inviteMember({
        email: value.email,
        role: value.role,
        organizationId: user.organizationId,
      })
      setIsSubmitting(false)
      setInviteSent(true)
      form.reset()
    },
  })

  const handleConfirmRemove = async () => {
    if (!removeTarget || !user.organizationId) return
    setIsRemoving(true)
    await authClient.organization.removeMember({
      memberIdOrEmail: removeTarget.email,
      organizationId: user.organizationId,
    })
    setIsRemoving(false)
    setRemoveTarget(undefined)
    await refetchMembers()
  }

  const memberDisplayName = (member: {
    firstName: string | undefined
    lastName: string | undefined
    username: string | undefined
    email: string
  }): string => {
    if (member.firstName && member.lastName)
      return `${member.firstName} ${member.lastName}`
    return member.username ?? member.email
  }

  return (
    <>
      <div className="flex h-full w-full flex-col gap-6">
        {isOwner && (
          <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
            <div className="flex w-full flex-col gap-6 rounded-lg p-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-medium text-primary">
                  {t('settings.team.inviteTitle')}
                </h2>
                <p className="text-sm font-normal text-primary/60">
                  {t('settings.team.inviteDescription')}
                </p>
              </div>

              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  form.handleSubmit()
                }}
              >
                <div className="flex gap-3">
                  <form.Field
                    name="email"
                    children={(field) => (
                      <Input
                        type="email"
                        placeholder={t('settings.team.emailPlaceholder')}
                        required
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className="w-64 bg-transparent"
                      />
                    )}
                  />

                  <form.Field
                    name="role"
                    children={(field) => (
                      <Select
                        value={field.state.value}
                        onValueChange={(v) =>
                          field.handleChange(v as 'member' | 'admin')
                        }
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">
                            {t('settings.team.roleMember')}
                          </SelectItem>
                          <SelectItem value="admin">
                            {t('settings.team.roleAdmin')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {inviteSent && (
                  <p className="text-sm text-primary/60">
                    {t('settings.team.inviteSent')}
                  </p>
                )}

                <Button
                  type="submit"
                  size="sm"
                  className="w-fit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    t('settings.team.invite')
                  )}
                </Button>
              </form>
            </div>

            <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
              <p className="text-sm font-normal text-primary/60">
                {t('settings.team.hint')}
              </p>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex w-full flex-col gap-4 rounded-lg p-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-medium text-primary">
                {t('settings.team.membersTitle')}
              </h2>
            </div>

            <div className="flex flex-col gap-2">
              {(members ?? []).map((member) => (
                <div
                  key={member.userId}
                  className="flex items-center justify-between rounded-md border border-border px-4 py-3"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-primary">
                        {memberDisplayName(member)}
                      </p>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          roleBadgeClass(member.role),
                        )}
                      >
                        {t(roleTranslationKey(member.role))}
                      </span>
                    </div>
                    <p className="text-xs text-primary/60">{member.email}</p>
                  </div>

                  {isOwner && member.userId !== user._id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-primary/60 hover:text-destructive"
                      onClick={() =>
                        setRemoveTarget({
                          userId: member.userId,
                          email: member.email,
                          displayName: memberDisplayName(member),
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {removeTarget !== undefined && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-lg">
            <h3 className="mb-1 text-base font-semibold text-primary">
              {t('settings.team.removeDialogTitle')}
            </h3>
            <p className="mb-6 text-sm text-primary/60">
              {t('settings.team.removeDialogDescription', {
                name: removeTarget.displayName,
              })}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRemoveTarget(undefined)}
                disabled={isRemoving}
              >
                {t('settings.team.cancel')}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleConfirmRemove}
                disabled={isRemoving}
              >
                {isRemoving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('settings.team.removeConfirm')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
